import { useEffect, useState } from "react";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import type { IdeaGroupPatch } from "@/hooks/use-offer-idea-groups";
import type { DBOfferIdeaGroup } from "@/lib/db-types";
import { usePointerDrag } from "./use-pointer-drag";

const MIN_W = 280;
const MIN_H = 200;

interface IdeaGroupFrameProps {
  group: DBOfferIdeaGroup;
  readOnly: boolean;
  /** Gruppen flyttet; kortene i den flyttes med. */
  onMove: (group: DBOfferIdeaGroup, dx: number, dy: number) => void;
  onPatch: (patch: IdeaGroupPatch) => void;
  onDelete: () => void;
}

/**
 * En gruppe på Idé-tavlen (B7): en ramme med navn. Kort som slippes inni
 * hører til gruppen og flyttes med den. Dras i toppen, størrelse i hjørnet.
 */
export function IdeaGroupFrame({ group, readOnly, onMove, onPatch, onDelete }: IdeaGroupFrameProps) {
  const move = usePointerDrag(({ dx, dy }) => onMove(group, dx, dy), readOnly);
  const resize = usePointerDrag(({ dx, dy }) =>
    onPatch({
      width: Math.max(MIN_W, Math.round(group.width + dx)),
      height: Math.max(MIN_H, Math.round(group.height + dy)),
    }),
    readOnly,
  );
  const [label, setLabel] = useState(group.label);
  useEffect(() => setLabel(group.label), [group.label]);

  const dx = move.offset?.dx ?? 0;
  const dy = move.offset?.dy ?? 0;

  return (
    <section
      aria-label={`Gruppe ${group.label || "uten navn"}`}
      className="absolute rounded-lg border-[1.5px] border-dashed border-foreground/30 bg-muted/40"
      style={{
        left: group.x + dx,
        top: group.y + dy,
        width: Math.max(MIN_W, group.width + (resize.offset?.dx ?? 0)),
        height: Math.max(MIN_H, group.height + (resize.offset?.dy ?? 0)),
      }}
    >
      <div className="flex items-center gap-1.5 px-2 py-1">
        <div
          {...move.handlers}
          className={cn("p-0.5 text-muted-foreground touch-none", !readOnly && "cursor-grab")}
          aria-hidden
        >
          <SrIcon name="grip-vertical" className="w-3.5 h-3.5" />
        </div>
        <input
          value={label}
          readOnly={readOnly}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => label !== group.label && onPatch({ label })}
          placeholder="Navn på gruppen"
          aria-label="Navn på gruppen"
          className="flex-1 min-w-0 bg-transparent text-sm font-medium focus:outline-none focus:bg-background rounded-sm px-1"
        />
        {!readOnly && (
          <button
            type="button"
            onClick={onDelete}
            className="p-0.5 rounded-sm text-muted-foreground hover:text-destructive"
            aria-label={`Slett gruppen ${group.label || "uten navn"}`}
          >
            <SrIcon name="trash-2" className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {!readOnly && (
        <div
          {...resize.handlers}
          className="absolute right-0 bottom-0 w-4 h-4 cursor-nwse-resize touch-none"
          aria-hidden
        >
          <span className="absolute right-1 bottom-1 w-2 h-2 border-r-2 border-b-2 border-foreground/40" />
        </div>
      )}
    </section>
  );
}
